import { useState } from "react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { StatusBadge, PriorityBadge, Avatar } from "../badges";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import { Plus, Flag } from "lucide-react";

export function TaskCreateDialog({ project, users, requirements, tasks, reload, defaultStatus = "todo" }) {
  const [open, setOpen] = useState(false);
  const empty = { title: "", description: "", status: defaultStatus, priority: "P2", assignee_id: null, start_date: "", due_date: "", requirement_id: null, dependencies: [] };
  const [form, setForm] = useState(empty);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${project.id}/tasks`, form);
      toast.success("Task created");
      setOpen(false); setForm(empty);
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-task-btn"><Plus className="h-4 w-4 mr-1" /> New Task</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">New Task</DialogTitle></DialogHeader>
        <form onSubmit={create} className="space-y-4">
          <div className="space-y-1.5"><Label>Title</Label>
            <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
          <div className="space-y-1.5"><Label>Description</Label>
            <Textarea data-testid="task-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["P0", "P1", "P2", "P3"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Assignee</Label>
              <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Start date</Label>
              <Input type="date" data-testid="task-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Due date</Label>
              <Input type="date" data-testid="task-due-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Linked requirement</Label>
              <Select value={form.requirement_id || "none"} onValueChange={(v) => setForm({ ...form, requirement_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="task-requirement-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {requirements.map((r) => <SelectItem key={r.id} value={r.id}>{r.rid} — {r.title.slice(0, 30)}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Depends on (predecessor)</Label>
              <Select value={form.dependencies[0]?.task_id || "none"}
                onValueChange={(v) => setForm({ ...form, dependencies: v === "none" ? [] : [{ task_id: v, type: "FS" }] })}>
                <SelectTrigger data-testid="task-dependency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.tid} — {t.title.slice(0, 30)}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <Button type="submit" data-testid="task-create-submit-btn" className="w-full">Create Task</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_ORDER = ["todo", "in_progress", "blocked", "testing", "completed"];

export default function TasksTab({ project, tasks, requirements, users, reload, openTask }) {
  const [filter, setFilter] = useState("all");
  const userById = (id) => users.find((u) => u.id === id);
  const taskById = (id) => tasks.find((t) => t.id === id);

  const shown = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
  const sorted = [...shown].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));

  return (
    <div className="space-y-4" data-testid="tasks-tab-content">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <TaskCreateDialog project={project} users={users} requirements={requirements} tasks={tasks} reload={reload} />
      </div>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-border bg-card text-left">
              {["ID", "Title", "Status", "Priority", "Assignee", "Dates", "Dependencies", "Progress"].map((h) => (
                <th key={h} className="px-4 py-2.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((t) => (
              <tr key={t.id} className="hover:bg-accent/40 cursor-pointer transition-colors" data-testid="task-card-item"
                onClick={() => openTask(t.id)}>
                <td className="px-4 py-3 font-mono text-xs text-primary whitespace-nowrap">
                  {t.milestone && <Flag className="h-3 w-3 inline mr-1 text-amber-500" />}{t.tid}
                </td>
                <td className="px-4 py-3 font-medium max-w-[280px] truncate">{t.title}</td>
                <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                <td className="px-4 py-3">
                  {t.assignee_id ? (
                    <span className="flex items-center gap-1.5"><Avatar name={userById(t.assignee_id)?.name} size="xs" />
                      <span className="text-xs">{userById(t.assignee_id)?.name?.split(" ")[0]}</span></span>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground whitespace-nowrap">{t.start_date || "—"} → {t.due_date || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {(t.dependencies || []).map((d) => (
                      <span key={d.task_id} className="text-[10px] font-mono border border-border rounded px-1 py-0.5 text-muted-foreground">
                        {taskById(d.task_id)?.tid || "?"} <span className="text-primary">{d.type}</span>
                      </span>
                    ))}
                    {!(t.dependencies || []).length && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3 w-32">
                  <div className="flex items-center gap-2">
                    <Progress value={t.progress} className="h-1.5 w-16" />
                    <span className="text-[10px] font-mono text-muted-foreground">{t.progress}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {!sorted.length && <tr><td colSpan={8} className="px-4 py-6 text-muted-foreground text-sm">No tasks.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
